import {
    Box, Card, Link as MuiLink,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    TextField,
    Chip,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import digitalDateCardService from "../../../services/digitalDateCardService";
import userService from "../../../services/userService";
import { IDigitalDateCardRow } from "../../../types/DigitalCard";
import { DigitalCardChangeCategory } from "../../../constants/DigitalCardChangeCategory";
import { formatTimestamp } from "../../../utils/timestampFormatter";
import DigitalCardRequestChangesModal from "./DigitalCardRequestChangesModal";

// A single row in the review queue, normalized down to what the table needs. This tab is
// digital-card submissions only — in-person physical orders live under "New Date Card Orders"
// (DateCardsList.tsx) and never belong here.
type QueueRow = {
    key: string;
    // A digital card's design comes from either the template catalog (templateId set) or a
    // custom upload (upload_custom_design_page.dart calls saveTemplate('Custom', 'Custom', url)
    // without an id, so templateId is absent). generatedImageUrl — the final rendered card — is
    // what admin should look at either way; isTemplate only drives the label.
    isTemplate: boolean;
    designLabel: string;
    designUrl?: string;
    cardMessage: string;
    profileUrl: string;
    timeStamp: string;
    dateCardDocId: string;
    // DateCardOrders/{dateCardDocId}/DigitalCards/{cardId} — a user can have more than one
    // pending card now, so approve/requestChanges need to target the right one, not just the user.
    cardId: string;
    // The user's account name (UserProfile.name), fetched separately since it's keyed off
    // dateCardDocId (== the user's Firebase Auth UID) rather than living on the card itself.
    // Undefined while loading; null if no matching UserProfile doc was found.
    userName?: string | null;
};

// DateCardOrders/{uid} docs are keyed by the user's Firebase Auth UID, which is also the
// UserProfile doc id — see IDigitalDateCardRow.dateCardDocId.
function adminUserUrl(userId: string): string {
    return `/users/${userId}`;
}

function toQueueRows(digitalCards: IDigitalDateCardRow[]): QueueRow[] {
    return digitalCards
        .map((card) => {
            const isTemplate = Boolean(card.templateId);
            return {
                // A user can now have more than one pending card, so the row key has to be
                // per-card, not per-user.
                key: card.cardId,
                isTemplate,
                designLabel: isTemplate ? 'Template design' : 'Uploaded design',
                designUrl: card.generatedImageUrl,
                cardMessage: card.cardMessage,
                profileUrl: card.profileUrl,
                timeStamp: card.timeStamp,
                dateCardDocId: card.dateCardDocId,
                cardId: card.cardId,
            };
        })
        .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime());
}

export const DigitalDateCardsList: React.FC<{
    pageSize?: 10 | 25 | 50 | 100;
}> = ({ pageSize = 25 }) => {
    const [rows, setRows] = useState<QueueRow[]>([]);
    const [filteredRows, setFilteredRows] = useState<QueueRow[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [approvingKey, setApprovingKey] = useState<string | null>(null);
    const [requestChangesTarget, setRequestChangesTarget] = useState<QueueRow | null>(null);

    const loadRows = useCallback(async () => {
        const digitalCards = await digitalDateCardService.getPendingReviewDigitalCards();
        const merged = toQueueRows(digitalCards);
        setRows(merged);
        setFilteredRows(
            searchTerm
                ? merged.filter((r) => r.profileUrl?.toLowerCase().includes(searchTerm))
                : merged
        );

        // Account names load separately (and asynchronously per row) so the review queue
        // itself isn't blocked on a UserProfile fetch per card.
        const withNames = await Promise.all(
            merged.map(async (row) => {
                const profile = await userService.getUserProfile(row.dateCardDocId);
                return { ...row, userName: profile?.name ?? null };
            })
        );
        setRows(withNames);
        setFilteredRows((current) =>
            current.map((row) => withNames.find((r) => r.key === row.key) ?? row)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        void loadRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        setFilteredRows(rows.filter((r) => r.profileUrl?.toLowerCase().includes(term)));
    };

    async function handleApprove(row: QueueRow) {
        setApprovingKey(row.key);
        try {
            // Template designs are already "approved" from the user's perspective — only
            // custom uploads are actually waiting on admin review, so only those get notified.
            await digitalDateCardService.approve(row.dateCardDocId, row.cardId, !row.isTemplate);
            await loadRows();
        } finally {
            setApprovingKey(null);
        }
    }

    async function handleRequestChangesSubmit(reason: string, categories: DigitalCardChangeCategory[]) {
        if (!requestChangesTarget) return;
        const row = requestChangesTarget;
        await digitalDateCardService.requestChanges(
            row.dateCardDocId,
            row.cardId,
            categories,
            reason,
        );
        setRequestChangesTarget(null);
        await loadRows();
    }

    return (
        <Box>
            <Typography variant="h1" sx={{ mb: 4 }}>
                Date Card Review
            </Typography>

            <Box sx={{ mb: 4 }}>
                <TextField
                    label="Search by Profile URL"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ width: '300px' }}
                />
            </Box>

            <Card sx={{ mb: 4 }}>
                <Box sx={{ width: '100%' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Design</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Card intro message</TableCell>
                                    <TableCell>Profile Url</TableCell>
                                    <TableCell>Admin URL</TableCell>
                                    <TableCell>Order created</TableCell>
                                    <TableCell>Order status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRows
                                    .slice(page * pageSize, page * pageSize + pageSize)
                                    .map((row) => (
                                        <TableRow key={row.key} sx={{ cursor: 'default', textDecoration: 'none' }}>
                                            <TableCell>
                                                <Chip
                                                    label={row.isTemplate ? 'Template' : 'Uploaded'}
                                                    size="small"
                                                    color={row.isTemplate ? 'default' : 'secondary'}
                                                    sx={{ display: 'block', width: 'fit-content', mb: 0.5 }}
                                                />
                                                {row.designUrl && (
                                                    <MuiLink href={row.designUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
                                                        {row.designLabel}
                                                    </MuiLink>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {row.userName === undefined ? '…' : (row.userName ?? '—')}
                                            </TableCell>
                                            <TableCell>{row.cardMessage}</TableCell>
                                            <TableCell>
                                                <MuiLink href={row.profileUrl} onClick={(e) => e.stopPropagation()} target={row.profileUrl}>
                                                    {row.profileUrl}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>
                                                {/* Opens in a new tab so the reviewer keeps this review queue open; the
                                                    profile page itself is where account-level changes get requested. */}
                                                <MuiLink
                                                    href={adminUserUrl(row.dateCardDocId)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    View Admin Profile
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{formatTimestamp(row.timeStamp)}</TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={false}
                                                            disabled={approvingKey === row.key}
                                                            onChange={() => handleApprove(row)}
                                                            inputProps={{ 'aria-label': 'Accepted' }}
                                                        />
                                                    }
                                                    label="Accepted"
                                                />
                                                <Button
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => setRequestChangesTarget(row)}
                                                >
                                                    Request Changes
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component={"div"}
                        count={filteredRows.length}
                        page={page}
                        rowsPerPage={pageSize}
                        onPageChange={(event, newPage) => setPage(newPage)} />
                </Box>
            </Card>

            <DigitalCardRequestChangesModal
                isOpen={requestChangesTarget !== null}
                designLabel={requestChangesTarget?.designLabel ?? null}
                isTemplate={requestChangesTarget?.isTemplate ?? false}
                onClose={() => setRequestChangesTarget(null)}
                onSubmit={handleRequestChangesSubmit}
            />
        </Box>
    );
};
